import React, { useEffect, useMemo, useRef } from "react";
import { axiosGetForCombo } from "../utils/AxiosUtils";

import { debounce } from "@mui/material";
import SaitCombo from "./SaitCombo";
import { EQUAL, STARTS_WITH } from "../utils/FiltersOperators";
import SaitComboFilter from "./SaitComboFilter";
import PropTypes from "prop-types";

SaitDynamicCombo.propTypes = {
	label: PropTypes.string,
	name: PropTypes.arrayOf(
		PropTypes.shape({
			storeName: PropTypes.string,
			comboName: PropTypes.string,
		})
	).isRequired,
	description: PropTypes.string,
	findParameters: PropTypes.shape({
		findFunction: PropTypes.string,
		findFilters: PropTypes.array,
		orderBy: PropTypes.arrayOf(
			PropTypes.shape({
				property: PropTypes.string,
				direction: PropTypes.string,
			})
		),
	}),
	findFunction: PropTypes.string,
	filterField: PropTypes.string,
	items: PropTypes.array,
	setItems: PropTypes.func,
	filterCombo: PropTypes.bool,
	index: PropTypes.number,
	value: PropTypes.object,
	valueFilters: PropTypes.array,
	setValueFilters: PropTypes.func,
	stateElement: PropTypes.object,
	setFunction: PropTypes.func,
	afterChange: PropTypes.func,
	disabled: PropTypes.bool,
	readOnly: PropTypes.bool,
	disabledAll: PropTypes.bool,
	required: PropTypes.bool,
	error: PropTypes.bool,
	size: PropTypes.string,
	sx: PropTypes.object,
};

/**
 * Combo con ricerca lato server: ad ogni digitazione viene eseguita la find con filtro STARTS_WITH su filterField.
 * La find si configura con findParameters { findFunction, findFilters, orderBy } (come le altre COMBO) oppure,
 * per retrocompatibilità, con findFunction direttamente sull'oggetto.
 * filterField è il campo su cui viene applicato lo STARTS_WITH (default: comboName del primo name).
 * orderBy di default: filterField ASC.
 * items / setItems sono opzionali: se non specificati gli items vengono gestiti internamente
 * e, nelle EditPanel, il valore già presente nello stateElement viene caricato in automatico (EQUAL sui campi del name).
 *
 * @param {{
 *  label?: string,
 *  name: { storeName: string, comboName: string }[],
 *  description?: string,
 *  findParameters?: { findFunction: string, findFilters?: object[], orderBy?: { property: string, direction: string }[] },
 *  findFunction?: string,
 *  filterField?: string,
 *  items?: object[],
 *  setItems?: (items: object[]) => void,
 *  filterCombo?: boolean,
 *  index?: number,
 *  value?: object,
 *  valueFilters?: object[],
 *  setValueFilters?: (valueFilters: object[]) => void,
 *  stateElement?: object,
 *  setFunction?: (stateElement: object) => void,
 *  afterChange?: (selectedRow: object) => void,
 *  disabled?: boolean,
 *  readOnly?: boolean,
 *  disabledAll?: boolean,
 *  required?: boolean,
 *  error?: boolean,
 *  size?: string,
 *  sx?: object
 * }} props
 */
export default function SaitDynamicCombo(props) {
	const userInputRef = useRef(true); // Rileva se il cambio input viene dall'utente

	const [internalItems, setInternalItems] = React.useState([]);

	// Se gli items sono gestiti dall'esterno uso quelli, altrimenti lo state interno.
	// Gestirli internamente evita il re-render della pagina ad ogni ricerca
	const externalItems = typeof props.setItems === "function";
	const items = externalItems ? props.items : internalItems;

	const findFunction = props.findParameters?.findFunction || props.findFunction;
	const filterField = props.filterField || props.name?.[0]?.comboName;

	// Ref sempre aggiornato, così il debounce (creato una sola volta) usa i parametri correnti
	const latestProps = useRef();
	latestProps.current = { ...props, findFunction, filterField, externalItems };

	// Contatore delle richieste: le risposte arrivate fuori ordine vengono scartate
	const requestId = useRef(0);

	const setItems = (newItems) => {
		if (latestProps.current.externalItems) {
			latestProps.current.setItems(newItems);
		} else {
			setInternalItems(newItems);
		}
	};

	const updateCombo = (newInputValue) => {
		const currentRequest = ++requestId.current;

		if (typeof newInputValue === "undefined" || newInputValue === null || newInputValue.length === 0) {
			setItems([]);
			return;
		}

		const { findParameters, findFunction, filterField } = latestProps.current;

		axiosGetForCombo(
			findFunction,
			[
				...(findParameters?.findFilters || []),
				{
					field: filterField,
					operator: STARTS_WITH,
					value: newInputValue,
				},
			],
			findParameters?.orderBy || [{ property: filterField, direction: "ASC" }],
			(newItems) => {
				if (currentRequest === requestId.current) {
					setItems(newItems || []);
				}
			},
		);
	};

	// Se cambiano i filtri fissi (es. combo dipendente da un altro campo) i risultati caricati non sono più validi
	const findFiltersKey = JSON.stringify(props.findParameters?.findFilters || []);
	const didMountRef = useRef(false);

	useEffect(() => {
		if (!didMountRef.current) {
			didMountRef.current = true;
			return;
		}
		if (!externalItems) {
			requestId.current++;
			setInternalItems([]);
		}
		// eslint-disable-next-line
	}, [findFiltersKey]);

	// Valore corrente della combo nelle EditPanel (es. record aperto in modifica): se non è tra gli items
	// lo carico con EQUAL sui campi del name, così la combo ne mostra la descrizione senza precaricamenti esterni
	const selectedValues = props.filterCombo ? [] : (props.name || []).map((item) => props.stateElement?.[item.storeName]);

	useEffect(() => {
		const isEmpty = (value) => value === undefined || value === null || value === "";

		if (externalItems || selectedValues.every(isEmpty)) {
			return;
		}

		const alreadyLoaded = items?.some((row) => props.name.every((item, index) => row[item.comboName] === selectedValues[index]));
		if (alreadyLoaded) {
			return;
		}

		const currentRequest = ++requestId.current;

		axiosGetForCombo(
			findFunction,
			[
				...(props.findParameters?.findFilters || []),
				...props.name
					.map((item, index) => ({
						field: item.comboName,
						operator: EQUAL,
						value: selectedValues[index],
					}))
					.filter((findFilter) => !isEmpty(findFilter.value)),
			],
			props.findParameters?.orderBy || [{ property: filterField, direction: "ASC" }],
			(newItems) => {
				if (currentRequest === requestId.current) {
					setItems(newItems || []);
				}
			},
		);
		// eslint-disable-next-line
	}, [JSON.stringify(selectedValues)]);

	// Debounce stabile tra i render
	const debounceCombo = useMemo(() => debounce((newInputValue) => updateCombo(newInputValue), 300), []);

	useEffect(() => {
		return () => debounceCombo.clear();
	}, [debounceCombo]);

	const onInputChange = (event, newInputValue, reason) => {
		// Cerco solo quando è l'utente a scrivere: il "reset" (selezione, blur, valore impostato da fuori)
		// riscrive nell'input la descrizione dell'elemento e non deve rilanciare la ricerca
		if (reason === "input" || reason === "clear") {
			userInputRef.current = true; // input da utente
			debounceCombo(newInputValue);
		} else {
			userInputRef.current = false; // es. reset, setValue programmatico
		}
	};

	const onChangeComboItem = (selectedRow, name, afterChange) => {
		if (!selectedRow) {
			selectedRow = {};
		}

		let newStateElement = { ...props.stateElement };
		name.forEach((item) => {
			newStateElement[item.storeName] = selectedRow[item.comboName];
		});

		props.setFunction(newStateElement);

		// Non chiama debounce se è stato un cambio programmato
		if (Object.keys(selectedRow).length > 0 && userInputRef.current) {
			debounceCombo(selectedRow[filterField]);
		}

		if (afterChange) {
			afterChange(selectedRow);
		}
	};

	const onChangeComboFilter = (selectedRow, index) => {
		const newFilters = [...props.valueFilters];

		if (selectedRow) {
			newFilters[index].value = selectedRow;
		} else {
			newFilters[index].value = {};
		}

		props.setValueFilters(newFilters);

		if (selectedRow) debounceCombo(selectedRow[filterField]);
	};

	// findParameters non va passato alle combo: farebbe partire il caricamento completo degli items
	// e disabiliterebbe la combo durante il caricamento (con conseguente perdita del focus)
	const { findParameters, setItems: _setItems, ...comboProps } = props;

	return props.filterCombo ? (
		<SaitComboFilter
			{...comboProps}
			key={props.index}
			label={props.label}
			value={props.value}
			name={props.name}
			onChange={(row) => onChangeComboFilter(row, props.index)}
			disabled={props.disabled}
			size={props.size}
			description={props.description}
			items={items}
			required={props.required}
			filterOptions={(x) => x}
			onInputChange={onInputChange}
		/>
	) : (
		<SaitCombo
			{...comboProps}
			key={props.index}
			label={props.label}
			value={props.stateElement}
			name={props.name}
			onChange={(row) => onChangeComboItem(row, props.name, props.afterChange)}
			disabled={props.disabled}
			readOnly={props.readOnly || props.disabledAll}
			size={props.size}
			description={props.description}
			items={items}
			required={props.required}
			filterOptions={(x) => x}
			onInputChange={onInputChange}
		/>
	);
}
